import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, ViewEncapsulation, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { NCatalogUi } from '@df/catalog-ui/df-catalog-ui.config';
import { IFilterDefinition, IListingConfig, IListingSetup, Listing } from '@df/catalog-ui/listing/listing';
import { ProductListing } from '@df/catalog-ui/listing/product-listing';
import { Category } from '@df/catalog/category/category';
import { IPageDataV2, IPageDataV3 } from '@df/catalog/cms/cms-model.interface';
import { IModelDataV2 } from '@df/catalog/model';
import { Product } from '@df/catalog/product/product';
import { App } from '@df/core/app';
import { DfModuleImageModule } from '@df/module-image/df-module-image.module';
import { ImageModule } from '@df/module-image/image.module';
import { ItemsInRowOptions } from '@df/ui/atomic/atomic-options';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { ICmsModel } from '@icms/core/resource/model/icms-model';
import { ICmsPage } from '@icms/core/resource/page/icms-page';
import { Subscription } from 'rxjs';
import { NBlogCategoryPageType } from '../blog/page/category-view/cms-blog-page-category-view.component';
import { NBlogPostPageType } from '../blog/page/post-view/cms-blog-post.namespace';
import { LocalPage } from '../page.decorator';

interface IPageComponentListingData {
    title?: string;
    subtitle?: string;
    ctaText?: string;
    ctaLink?: string;
    tags?: number[];
    type: 'product' | 'page' | 'category' | 'store';
    matching: 'group' | 'custom';
    rule?: string;
    group?: number;
    colspan: number;
    limit?: string;
    sort?: string;
    verbosity?: 1 | 2 | 3 | 4;
    initialStepsVisible?: number;
    infScrollStep?: number;
}
@ICmsComponent<PageComponentEditorialLayoutComponent>({
    id: 'editorial-layout',
    name: 'Editorial layout',
    tags: ['editorial', 'blog'],
    hidePreview: true,
    fields: [
        <NICmsContentField.IField>{
            key: 'title',
            type: NICmsContentField.EFieldType.text,
            title: 'Title',
            default: 'Lorem ipsum'
        },
        <NICmsContentField.IField>{
            key: 'subtitle',
            type: NICmsContentField.EFieldType.text,
            title: 'Subtitle',
            default: 'Lorem ipsum dolor sit amet'
        },
        <NICmsContentField.IField>{
            key: 'ctaText',
            type: NICmsContentField.EFieldType.text,
            title: 'CTA Text',
            default: 'Follow us'
        },
        <NICmsContentField.IField>{
            key: 'ctaLink',
            type: NICmsContentField.EFieldType.text,
            title: 'CTA Link'
        },

        {
            type: NICmsContentField.EFieldType.label,
            key: 'section-general',
            title: 'General'
        },
        <NICmsContentField.IChoiceField>{
            type: NICmsContentField.EFieldType.choice,
            title: 'Type',
            key: 'type',
            description: 'Listed object types.',
            options: [
                { key: 'page', title: 'Page or Post' },
                { key: 'product', title: 'Product' },
                { key: 'category', title: 'Category' },
                { key: 'store', title: 'Store' }
            ],
            default: 'page'
        },
        <NICmsContentField.IChoiceField>{
            type: NICmsContentField.EFieldType.choice,
            title: 'Matching Type',
            key: 'matching',
            description: 'How to pick models to show.',
            options: [
                { title: 'By group', key: 'group' },
                { title: 'Custom', key: 'custom' }
            ],
            default: 'group',
            canShow: (data: IPageComponentListingData) => data.type === 'page'
        },
        <NICmsContentField.IRelationField>{
            key: 'tags',
            type: NICmsContentField.EFieldType.relation,
            title: 'Additional blog categories',
            options: {
                title: 'Page',
                type: 'page',
                qty: Infinity,
                multiple: true,
                filter: (model: ICmsPage<IPageDataV3>) =>
                    model.rawData?.pageType === NBlogCategoryPageType.typeId || model.rawData?.pageType === NBlogPostPageType.typeId
            },
            canShow: (data: IPageComponentListingData) => data.type === 'page' && data.matching === 'group'
        },
        {
            type: NICmsContentField.EFieldType.text,
            title: 'Matching Rule',
            key: 'rule',
            description: 'Mongo query to run on data to identify requested models'
            // canShow: isCustomRule
            // sanitize: (value?: string): any => {
            //     if (value) {
            //         const fixed = this.fixJsonString(value);
            //         if (fixed) {
            //             return fixed;
            //         } else {
            //             throw new Error('Invalid JSON');
            //         }
            //     } else {
            //         return value;
            //     }
            // }
        },
        <NICmsContentField.IChoiceField>{
            type: NICmsContentField.EFieldType.choice,
            title: 'Items In Row',
            key: 'colspan',
            options: ItemsInRowOptions,
            default: 'c-3-set'
        },
        {
            type: NICmsContentField.EFieldType.text,
            title: 'Limit',
            key: 'limit',
            description: 'Max number of items to display'
            // sanitize: (value?: string): string | undefined => {
            //     return value ? value.replace('[D]', '') : value;
            // }
        },
        {
            type: NICmsContentField.EFieldType.number,
            title: 'Initial Steps loaded',
            key: 'initialStepsVisible',
            description: 'Steps showing up at page load'
        },
        {
            type: NICmsContentField.EFieldType.number,
            title: 'Step Size',
            key: 'infScrollStep',
            description: 'Number of items loaded per infinite scroll step.'
        },
        {
            type: NICmsContentField.EFieldType.text,
            title: 'Sort Order',
            key: 'sort',
            description: `Sort order in mongodb syntax like {name : 1, age : -1}`
            // sanitize: (value?: string): any => {
            //     if (value) {
            //         const fixed = this.fixJsonString(value);
            //         if (fixed) {
            //             return fixed;
            //         } else {
            //             throw new Error('Invalid JSON');
            //         }
            //     } else {
            //         return value;
            //     }
            // }
        },
        {
            type: NICmsContentField.EFieldType.number,
            key: 'verbosity',
            title: 'Verbosity',
            description: 'How detailed data should be returned',
            default: 2
            // sanitize: (value?: number) => {
            //     if (value < 1 || value > 4) {
            //         throw new Error('Invalid value. Available values: 1, 2, 3 or 4');
            //     }

            //     return value;
            // }
        }
    ]
})
@Component({
    selector: 'editorial-layout',
    templateUrl: './editorial-layout.html',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        SizeClassDirective,
        ICmsClientModule,
        HrefComponent,
        NgClass,
        DfModuleImageModule,
        ImageModule,
        TranslationModule,
        RouterLink
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PageComponentEditorialLayoutComponent extends UiComponent implements NICmsContentComponent.IComponent {
    @ICmsContent({
        type: 'component'
    })
    component!: IPageComponentListingData;
    /**
     * Listing model
     */
    listing: Listing | ProductListing | undefined;

    /**
     * Listing model subscription
     */
    listingSubscription?: Subscription;

    /**
     * Last data hash
     */
    private lastDataHash = '';

    private catalogConfig = inject(NCatalogUi.OPTIONS);

    constructor(private app: App) {
        super();
    }

    protected getListingModelIds(models: ICmsPage<IPageDataV2>[]): number[] {
        return models.sort((a, b) => (b.data?.createdAt || 0) - (a.data?.createdAt || 0)).map(i => i.id);
    }
    get listingAsBlogPosts(): Listing<ICmsPage<IPageDataV2>> {
        return this.listing as any as Listing<ICmsPage<IPageDataV2>>;
    }

    protected prepare(): Promise<ICmsModel<IModelDataV2>[]> | void {
        try {
            this.unsubscribe();
            if (this.component?.type) {
                const promise: Promise<ICmsModel<IModelDataV2>[]> = this.isCustomRule(this.component)
                    ? // find mofules for custom rule
                      (this.app.resources[this.component.type].find({
                          filter: this.component.rule ? JSON.parse(this.component.rule) : undefined,
                          verbosity: this.component.verbosity || 2,
                          sort: this.component.sort && JSON.parse(this.component.sort),
                          limit: (this.component.limit && parseInt(this.component.limit, 10)) || undefined
                      }) as Promise<ICmsModel<IModelDataV2>[]>)
                    : // load group for regular rule
                      (new Promise((resolve, reject) => {
                          if (this.component.tags) {
                              const pages = this.app.resources.page.getAll(this.component.tags);
                              this.app.resources.page.loadAllIds(this.component.tags, 2).then(() => {
                                  if (pages) {
                                      resolve(pages);
                                  } else {
                                      reject('Missing pages');
                                  }
                              });
                          } else {
                              reject('Missing pages ids');
                          }
                      }) as Promise<ICmsModel<IModelDataV2>[]>);
                promise
                    .then(models => {
                        const ops = this.getListingOptions();
                        const ids = this.getListingModelIds(models as LocalPage<IPageDataV2<NBlogCategoryPageType.ITypeData>>[]);
                        this.listing =
                            this.component.type === 'product'
                                ? new ProductListing(ops, ids, this.getListingSetup() as unknown as IListingSetup<Product>)
                                : new Listing(ops, ids, this.getListingSetup());
                        this.listingSubscription = this.listing.subscribe(() => this.onListingChange());
                        this.onListingCreated();
                    })
                    .catch(e => {
                        this.app.logger.error(e);
                        this.listing = undefined;
                        this.onListingCreated();
                    });
                return promise;
            } else {
                this.listing = undefined;
                this.onListingCreated();
            }
        } catch (err) {
            // skip silently - we are fine with errors while editing
        }
    }

    override ngOnInit() {
        this.syncHash();
        super.ngOnInit();
    }

    /**
     * Cleanup
     */
    override ngOnDestroy() {
        super.ngOnDestroy();

        this.unsubscribe();
    }

    /**
     * Syncs hash and informes if changed
     */
    private syncHash(): boolean {
        const dataHash = this.toJSONScopeData(this.component);
        if (this.lastDataHash === dataHash) {
            return false;
        }
        this.lastDataHash = dataHash;
        return true;
    }

    protected load() {
        return this.prepare();
    }

    protected getListingSetup(): IListingSetup {
        return {
            filtersDefinitions: this.getFilters()
        };
    }

    protected getListingOptions(): IListingConfig {
        return {
            initialStepsVisible: this.component.initialStepsVisible || undefined,
            type: this.component.type,
            source: this.component,
            infScrollStep: this.component.infScrollStep || this.catalogConfig.listing.cms.infScrollStep,
            storage: true
        };
    }

    get listingAsProducts(): ProductListing {
        return <ProductListing>this.listing;
    }

    get listingAsPages(): Listing<ICmsPage<IPageDataV3>> {
        return this.listing as any as Listing<ICmsPage<IPageDataV3>>;
    }

    get listingAsCategories(): Listing<Category> {
        return this.listing as any as Listing<Category>;
    }

    getFilters(): (string | IFilterDefinition)[] {
        return [];
    }

    onListingChange(): void {
        this.detectChanges();
    }

    trackByProductId(index: number, model: Product) {
        return model.id;
    }

    trackByPageId(index: number, model: ICmsPage) {
        return model.id;
    }

    trackByCategoryId(index: number, model: Category) {
        return model.id;
    }

    protected onListingCreated(): void {
        this.detectChanges();
    }

    /**
     * Unsubscribes listing changes if subscription exists
     */
    protected unsubscribe() {
        if (this.listingSubscription) {
            this.listingSubscription.unsubscribe();
        }
    }

    private toJSONScopeData(data) {
        return JSON.stringify(data, function (key, value) {
            if (typeof key === 'string') {
                if (key[0] === '_') {
                    return undefined;
                }
                if (key === 'uniqueId') {
                    return undefined;
                }
            }

            return value;

            // if (i !== 0 && typeof censor === 'object' && typeof value == 'object' && censor == value) return '[Circular]';

            // if (i >= 29)
            //     // seems to be a harded maximum of 30 serialized objects?
            //     return '[Unknown]';

            // ++i; // so we know we aren't using the original object anymore

            // return value;
        });
    }

    private fixJsonString(data: string): string | undefined {
        try {
            return JSON.stringify(JSON.parse(data));
        } catch (e) {
            // console.log(e);
            try {
                // tslint:disable:no-eval
                const ob = eval('(' + data + ')');

                // tslint:enable:no-eval
                if (typeof ob === 'object') {
                    return JSON.stringify(ob);
                }
            } catch (e) {
                // console.log(e);
            }
        }
        return undefined;
    }

    isCustomRule = (data: IPageComponentListingData) => data.type !== 'page' || data.matching === 'custom';
}
